import {
  useMutation,
  useQueryClient,
  type InfiniteData
} from '@tanstack/react-query'
import {
  userNotificationsControllerMarkAsRead,
  userNotificationsControllerMarkAllAsRead,
  userNotificationsControllerMarkAsClicked,
  userNotificationsControllerDismiss
} from 'api.gen'
import type { ListUserNotificationsResponse } from '../types'

type NotificationsCache = InfiniteData<ListUserNotificationsResponse>

const NOTIFICATIONS_KEY = ['user-notifications']
const UNREAD_COUNT_KEY = ['user-notifications-unread-count']

export const useMarkAsRead = () => {
  const queryClient = useQueryClient()

  const invalidateNotifications = () => {
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })
    queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
  }

  const updateNotificationInCache = (
    id: string,
    updater: (
      n: ListUserNotificationsResponse['data'][number]
    ) => ListUserNotificationsResponse['data'][number]
  ) => {
    queryClient.setQueryData<NotificationsCache>(NOTIFICATIONS_KEY, old => {
      if (!old) return old
      return {
        ...old,
        pages: old.pages.map(page => ({
          ...page,
          data: page.data.map(n => (n.id === id ? updater(n) : n))
        }))
      }
    })
  }

  const removeNotificationFromCache = (id: string) => {
    queryClient.setQueryData<NotificationsCache>(NOTIFICATIONS_KEY, old => {
      if (!old) return old
      return {
        ...old,
        pages: old.pages.map(page => ({
          ...page,
          data: page.data.filter(n => n.id !== id),
          meta: { ...page.meta, total: page.meta.total - 1 }
        }))
      }
    })
  }

  const decrementUnreadCount = () => {
    queryClient.setQueryData<{ count: number }>(UNREAD_COUNT_KEY, old => {
      if (!old) return old
      return { count: Math.max(0, old.count - 1) }
    })
  }

  const markAsRead = useMutation({
    mutationFn: (id: string) => userNotificationsControllerMarkAsRead(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY })
      const previous =
        queryClient.getQueryData<NotificationsCache>(NOTIFICATIONS_KEY)
      const previousCount = queryClient.getQueryData<{ count: number }>(
        UNREAD_COUNT_KEY
      )
      updateNotificationInCache(id, n => ({
        ...n,
        isRead: true,
        readAt: new Date().toISOString()
      }))
      decrementUnreadCount()
      return { previous, previousCount }
    },
    onError: (_err, _id, context) => {
      if (context?.previous)
        queryClient.setQueryData(NOTIFICATIONS_KEY, context.previous)
      if (context?.previousCount)
        queryClient.setQueryData(UNREAD_COUNT_KEY, context.previousCount)
    },
    onSettled: invalidateNotifications
  })

  const markAllAsRead = useMutation({
    mutationFn: () => userNotificationsControllerMarkAllAsRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY })
      const previous =
        queryClient.getQueryData<NotificationsCache>(NOTIFICATIONS_KEY)
      const previousCount = queryClient.getQueryData<{ count: number }>(
        UNREAD_COUNT_KEY
      )
      queryClient.setQueryData<NotificationsCache>(NOTIFICATIONS_KEY, old => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            data: page.data.map(n => ({
              ...n,
              isRead: true,
              readAt: new Date().toISOString()
            }))
          }))
        }
      })
      queryClient.setQueryData<{ count: number }>(UNREAD_COUNT_KEY, {
        count: 0
      })
      return { previous, previousCount }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(NOTIFICATIONS_KEY, context.previous)
      if (context?.previousCount)
        queryClient.setQueryData(UNREAD_COUNT_KEY, context.previousCount)
    },
    onSettled: invalidateNotifications
  })

  const markAsClicked = useMutation({
    mutationFn: (id: string) => userNotificationsControllerMarkAsClicked(id),
    onSuccess: invalidateNotifications
  })

  const dismissNotification = useMutation({
    mutationFn: (id: string) => userNotificationsControllerDismiss(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY })
      const previous =
        queryClient.getQueryData<NotificationsCache>(NOTIFICATIONS_KEY)
      const previousCount = queryClient.getQueryData<{ count: number }>(
        UNREAD_COUNT_KEY
      )
      const wasUnread = previous?.pages.some(p =>
        p.data.some(n => n.id === id && !n.isRead)
      )
      removeNotificationFromCache(id)
      if (wasUnread) decrementUnreadCount()
      return { previous, previousCount }
    },
    onError: (_err, _id, context) => {
      if (context?.previous)
        queryClient.setQueryData(NOTIFICATIONS_KEY, context.previous)
      if (context?.previousCount)
        queryClient.setQueryData(UNREAD_COUNT_KEY, context.previousCount)
    },
    onSettled: invalidateNotifications
  })

  return {
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
    markAsClicked: markAsClicked.mutate,
    dismissNotification: dismissNotification.mutate,
    isMarkingAsRead: markAsRead.isPending,
    isMarkingAllAsRead: markAllAsRead.isPending
  }
}
